import { useState, useCallback, useEffect } from 'react';
import { Contract } from 'ethers';
import { useWalletConnector } from './useWalletConnector';
import { CHECK_IN_CONTRACT_ADDRESS, CHECK_IN_ABI } from '~/constants';
import { BSC_CHAIN_ID_HEX, BSC_CHAIN_CONFIG } from '~/constants';

export interface CheckInStats {
  count: number;
  lastCheckInTime: number;
  lastCheckInDate: Date | null;
}

export const useCheckIn = () => {
  const { provider, address, isConnected } = useWalletConnector();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<CheckInStats | null>(null);

  const ensureOpbnbNetwork = useCallback(async () => {
    if (!provider) return false;
    try {
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== parseInt(BSC_CHAIN_ID_HEX, 16)) {
        try {
          await provider.send('wallet_switchEthereumChain', [
            { chainId: BSC_CHAIN_ID_HEX },
          ]);
        } catch (error: any) {
          if (error.code === 4902) {
            await provider.send('wallet_addEthereumChain', [BSC_CHAIN_CONFIG]);
            await provider.send('wallet_switchEthereumChain', [
              { chainId: BSC_CHAIN_ID_HEX },
            ]);
          } else {
            throw error;
          }
        }
      }
      return true;
    } catch {
      return false;
    }
  }, [provider]);

  const getContract = useCallback(
    async (withSigner = false) => {
      if (!provider || !CHECK_IN_CONTRACT_ADDRESS) return null;

      try {
        await ensureOpbnbNetwork();
        if (withSigner) {
          const signer = await provider.getSigner();
          return new Contract(CHECK_IN_CONTRACT_ADDRESS, CHECK_IN_ABI, signer);
        }
        return new Contract(CHECK_IN_CONTRACT_ADDRESS, CHECK_IN_ABI, provider);
      } catch (err) {
        console.error('Failed to create contract instance:', err);
        return null;
      }
    },
    [provider, ensureOpbnbNetwork],
  );

  const fetchUserStats = useCallback(async () => {
    if (!address || !provider) return;

    try {
      setError(null);
      await ensureOpbnbNetwork();
      const contract = await getContract(false);
      if (!contract) return;

      const [count, lastTime] = await contract.getUserInfo(address);

      setUserStats({
        count: Number(count),
        lastCheckInTime: Number(lastTime),
        lastCheckInDate:
          lastTime > 0 ? new Date(Number(lastTime) * 1000) : null,
      });
    } catch (err) {
      console.error('Error fetching user stats:', err);
      // Don't set global error for background fetch, just log it
    }
  }, [address, provider, getContract, ensureOpbnbNetwork]);

  const checkIn = useCallback(async () => {
    if (!address || !provider) {
      setError('Wallet not connected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await ensureOpbnbNetwork();
      const contract = await getContract(true);
      if (!contract) throw new Error('Contract not initialized');

      const tx = await contract.checkIn();
      await tx.wait();

      // Refresh stats after successful check-in
      await fetchUserStats();

      return tx;
    } catch (err: any) {
      console.error('Check-in error:', err);
      if (err.message?.includes('already checked in this block')) {
        setError(
          'You have already checked in this block. Please wait for the next block.',
        );
      } else {
        setError(err.message || 'Failed to check in');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [address, provider, getContract, fetchUserStats, ensureOpbnbNetwork]);

  // Initial fetch when wallet connects
  useEffect(() => {
    if (isConnected && address) {
      fetchUserStats();
    } else {
      setUserStats(null);
    }
  }, [isConnected, address, fetchUserStats]);

  return {
    checkIn,
    fetchUserStats,
    userStats,
    isLoading,
    error,
    isConnected,
  };
};
